import { useRef, forwardRef, useImperativeHandle } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import React from 'react';
import ProjectCreateTableInfoV2 from './ProjectCreateTableInfoV2';
import ProjectCreateTableFinanceV2 from './ProjectCreateTableFinanceV2';
import ProjectCreateTableStaffV2 from './ProjectCreateTableStaffV2';
import ProjectCreateTableFinanceViewV2 from './ProjectCreateTableFinanceViewV2';
import ProjectCreateTableCommentV2 from './ProjectCreateTableCommentV2';
import { parseJwt } from '@utils/jwt.js';

const ProjectCreateTableV2 = forwardRef(({id}, ref) => {
  const token = localStorage.getItem('token');
  const parsed = token ? parseJwt(token) : null;
  const isAdmin = parsed?.isAdmin === 1 ? true : false;

  const projectInfoRef = useRef();
  const financeViewRef = useRef();
  const monthlySaleRef = useRef();
  const monthlyPurchaseRef = useRef();
  const monthlyExternalStaffRef = useRef();
  const monthlyManagementFeeRef = useRef();
  const projectStaffInfoRef = useRef();

  // 사업정보 조회 (state 조회)
  const getProjectInfo = () => {
    if (projectInfoRef.current) {
      const projectInfo = projectInfoRef.current.getProjectInfo();
      return projectInfo;
    }
  };

  // 매출 조회 (state 조회)
  const getMonthlySale = () => {
    if (monthlySaleRef.current) {
      const monthlySale = monthlySaleRef.current.getMonthlyFinance();
      return monthlySale;
    }
  };

  // 매입 조회 (state 조회)
  const getMonthlyPurchase = () => {
    if (monthlyPurchaseRef.current) {
      const monthlyPurchase = monthlyPurchaseRef.current.getMonthlyFinance();
      return monthlyPurchase;
    }
  };

  // 외부인건비 조회 (state 조회)
  const getMonthlyExternalStaff = () => {
    if (monthlyExternalStaffRef.current) {
      const monthlyExternalStaff = monthlyExternalStaffRef.current.getMonthlyFinance();
      return monthlyExternalStaff;
    }
  };

  // 판관비 조회 (state 조회)
  const getMonthlyManagementFee = () => {
    if (monthlyManagementFeeRef.current) {
      const monthlyManagementFee = monthlyManagementFeeRef.current.getMonthlyFinance();
      return monthlyManagementFee;
    }
  };

  // 인력투입현황 조회 (state 조회)
  const getProjectStaffInfo = () => {
    if (projectStaffInfoRef.current) {
      const projectStaffInfo = projectStaffInfoRef.current.getProjectStaffInfo();
      return projectStaffInfo;
    }
  };

  // 시작 년도 설정 (재정 정보)
  const setStartYear = (year) => {
    if (monthlySaleRef.current) {
      monthlySaleRef.current.setStartYear(year);
    }
    if (monthlyPurchaseRef.current) {
      monthlyPurchaseRef.current.setStartYear(year);
    }
    if (monthlyExternalStaffRef.current) {
      monthlyExternalStaffRef.current.setStartYear(year);
    }
    if (monthlyManagementFeeRef.current) {
      monthlyManagementFeeRef.current.setStartYear(year);
    }
  };
  
  // 종료 년도 설정 (재정 정보)
  const setEndYear = (year) => {
    if (monthlySaleRef.current) {
      monthlySaleRef.current.setEndYear(year);
    }
    if (monthlyPurchaseRef.current) {
      monthlyPurchaseRef.current.setEndYear(year);
    }
    if (monthlyExternalStaffRef.current) {
      monthlyExternalStaffRef.current.setEndYear(year);
    }
    if (monthlyManagementFeeRef.current) {
      monthlyManagementFeeRef.current.setEndYear(year);
    }
  };

  // 년도 선택 설정 (재정 정보)
  const setSelectedYear = (year) => {
    if (monthlySaleRef.current) {
      monthlySaleRef.current.setSelectedYear(year);
    }
    if (monthlyPurchaseRef.current) {
      monthlyPurchaseRef.current.setSelectedYear(year);
    }
    if (monthlyExternalStaffRef.current) {
      monthlyExternalStaffRef.current.setSelectedYear(year);
    }
    if (monthlyManagementFeeRef.current) {
      monthlyManagementFeeRef.current.setSelectedYear(year);
    }
  };

  // 실적현황 호출
  const callFinanceView = (financeType) => {
    if (financeViewRef.current) {
      financeViewRef.current.callFinanceView(financeType);
    }
  };

  useImperativeHandle(ref, () => ({
    getSubmitInfo: () => {
      return {
        projectInfo: getProjectInfo(),
        monthlySale: getMonthlySale(),
        monthlyPurchase: getMonthlyPurchase(),
        monthlyExternalStaff: getMonthlyExternalStaff(),
        monthlyManagementFee: getMonthlyManagementFee(),
        projectStaffInfo: getProjectStaffInfo(),
      }
    },
    setProjectInfo: (info) => {
      if (projectInfoRef.current) {
        projectInfoRef.current.setProjectInfo(info);
      }
    },
    setSaleInfo: (info) => {
      if (monthlySaleRef.current) {
        monthlySaleRef.current.setMonthlyFinance(info);
      }
    },
    setPurchaseInfo: (info) => {
      if (monthlyPurchaseRef.current) {
        monthlyPurchaseRef.current.setMonthlyFinance(info);
      }
    },
    setExternalStaffInfo: (info) => {
      if (monthlyExternalStaffRef.current) {
        monthlyExternalStaffRef.current.setMonthlyFinance(info);
      }
    },
    setManagementFeeInfo: (info) => {
      if (monthlyManagementFeeRef.current) {
        monthlyManagementFeeRef.current.setMonthlyFinance(info);
      }
    },
    setProjectStaffInfo: (info) => {
      if (projectStaffInfoRef.current) {
        projectStaffInfoRef.current.setProjectStaffInfo(info);
      }
    },
  }));

  return (
    <React.Fragment>

      {/* 사업정보 */}
      <ProjectCreateTableInfoV2 ref={projectInfoRef} setStartYearFinance={setStartYear} setEndYearFinance={setEndYear} setSelectedYearFinance={setSelectedYear} />

      {/* 실적현황 */}
      <ProjectCreateTableFinanceViewV2 
        ref={financeViewRef}
        getMonthlySale={getMonthlySale}
        getMonthlyPurchase={getMonthlyPurchase}
        getMonthlyExternalStaff={getMonthlyExternalStaff}
        getMonthlyManagementFee={getMonthlyManagementFee}
      />

      {/* 매출 */}
      <ProjectCreateTableFinanceV2 ref={monthlySaleRef} title="매출" getProjectInfo={getProjectInfo} callFinanceView={callFinanceView} isAdmin={isAdmin} />

      {/* 매입 */}
      <ProjectCreateTableFinanceV2 ref={monthlyPurchaseRef} title="매입" getProjectInfo={getProjectInfo} callFinanceView={callFinanceView} isAdmin={isAdmin} />

      {/* 외부인건비 */}
      <ProjectCreateTableFinanceV2 ref={monthlyExternalStaffRef} title="외부인건비" getProjectInfo={getProjectInfo} callFinanceView={callFinanceView} isAdmin={isAdmin} />

      {/* 판관비 */}
      <ProjectCreateTableFinanceV2 ref={monthlyManagementFeeRef} title="판관비" getProjectInfo={getProjectInfo} callFinanceView={callFinanceView} isAdmin={isAdmin} />

      {/* 인력투입현황 */}
      <ProjectCreateTableStaffV2 ref={projectStaffInfoRef} getProjectInfo={getProjectInfo} />

      {/* 진행현황 (상세화면 조회시) */}
      {id && <ProjectCreateTableCommentV2 id={id} />}
    </React.Fragment>
  );
});

export default ProjectCreateTableV2;
